import type { FC } from 'react';
import React, { Fragment, useEffect, useState } from 'react';
// We have deprecated unstated. Please use react-sweet-state instead
import { Subscribe } from 'unstated';
import { css, styled, cssMap } from '@compiled/react';

import { cssMap as adsCssMap } from '@atlaskit/css';
import { token } from '@atlaskit/tokens';

import type { IEditorPreloader } from '@confluence/edit-button/entry-points/types.ts';
import { BannerStateContainer } from '@confluence/banners/entry-points/BannerStateContainer.ts';
import { Attribution } from '@confluence/error-boundary/entry-points/Attribution.ts';
import { ErrorBoundary } from '@confluence/error-boundary/entry-points/ErrorBoundary.ts';
import {
	ATL_GENERAL,
	ATL_FOOTER,
} from '@confluence/web-item-location/entry-points/webFragmentLocations.ts';
import { PageLayout } from '@confluence/page-layout/entry-points/PageLayout.ts';
import type { SideNavView } from '@confluence/page-layout/entry-points/SideNavViewType.ts';
import { useATLGeneralWebPanelObserver } from '@confluence/web-panel-location/entry-points/useATLGeneralWebPanelObserver.ts';
import {
	WebPanelLocation,
	WebPanelLocationBatchProvider,
} from '@confluence/web-panel-location/entry-points/WebPanelLocation.ts';
import { useForgePageBannerObserver } from '@confluence/forge-ui/entry-points/useForgePageBannerObserver.ts';
import { StatsigConfigurations } from '@confluence/statsig-configurations/entry-points/StatsigConfigurations.ts';
import { ReturnToMobile } from '@confluence/return-to-mobile';
import { PERFORMANCE_SUBJECT_mainLayout } from '@confluence/performance/entry-points/constants.ts';
import { PerformanceEnd } from '@confluence/performance/entry-points/PerformanceEnd.ts';
import { useRightSidebarContext } from '@confluence/page-layout-context';
import { useIsCurrentPageLive } from '@confluence/live-pages-utils/entry-points/useIsCurrentPageLive.ts';
import { expValEquals } from '@confluence/feature-experiments';
import { fg } from '@confluence/feature-gating';
import { ConfluencePreviewPanelsProvider } from '@confluence/preview-panel-api/entry-points/ConfluencePreviewPanelsProvider.ts';
import { useIsSSROrHydrating } from '@confluence/ssr-utilities/entry-points/useIsSSROrHydrating.ts';
import { SuspenseWithDataFetcher } from '@confluence/query-preloader-tools/entry-points/SuspenseWithDataFetcher.ts';
import { PDF_EXPORT_PAGE } from '@confluence/named-routes/entry-points/routes.ts';

import { BreakoutSSRInlineScript } from './atl-general-breakout-ssr.tsx';
import { RenderPageBannerForgeApps, RenderBackgroundScriptApps } from './ForgeApps.tsx';
import type { MainLayoutQuery$data } from './__generated__/MainLayoutQuery.graphql.ts';

type MainLayoutScreenType = NonNullable<
	NonNullable<
		NonNullable<NonNullable<MainLayoutQuery$data['space']>['lookAndFeel']>['content']
	>['screen']
>;

const webPanelWrapperStyles = cssMap({
	showPanel: {
		display: 'block',
	},
	hidePanel: {
		display: 'none',
	},
});

const contentBodyWrapperStyles = css({
	marginTop: 0,
	boxSizing: 'border-box',
	clear: 'both',
	position: 'relative',
	width: '100%',
	display: 'flex',
	flex: '0 1 auto',
	minHeight: 'unset',
});

const fillContainerStyles = adsCssMap({
	container: {
		display: 'flex',
		flexDirection: 'column',
		flexWrap: 'nowrap',
		height: '100%',
		minHeight: 0,
	},
	body: {
		display: 'flex',
		flexDirection: 'column',
		flex: 1,
		minHeight: 0,
	},
});

const fixedWebPanelSpacerStyles = css({
	height: 'var(--atl-general-webpanel-height)',
});

const fixedForgeBannerSpacerStyles = css({
	height: 'var(--forge-page-banner-height)',
});

const mainContentContainerStyles = cssMap({
	default: {
		width: '100%',
		transition: 'width 300ms cubic-bezier(0.2, 0, 0, 1) 0s',
		flexFlow: 'row wrap',
		'@media print': {
			// eslint-disable-next-line @atlaskit/ui-styling-standard/no-important-styles -- Ignored via go/DSP-18766
			width: '100% !important',
		},
		marginLeft: token('space.0'),
	},
	addMinWidth: {
		minWidth: 700,
	},
});

const fixedWebPanelWrapperStyles = cssMap({
	default: {
		display: 'none',
		zIndex: 12, // needs to be above 10 so it covers floating sticky table rows
		width: '100%',
		position: 'fixed',
		backgroundColor: token('elevation.surface'),
		left: 'var(--leftSidebarWidth)',
		maxWidth: `calc( 100vw - var(--leftPanelWidth, 0px) - var(--leftSidebarWidth, 0px) - var(--rightPanelWidth, 0px) - var(--rightSidebarWidth, 0px) )`,
	},
	showPanel: {
		display: 'block',
	},
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled
const ContentBody = styled.div({
	backgroundColor: token('elevation.surface'),
	boxSizing: 'border-box',
	verticalAlign: 'top',
	minHeight: 'initial',
	padding: 0,
	width: '100%',
});

type EditorPreloaderType = (config: IEditorPreloader) => JSX.Element | null;

const BANNER_STATE_CONTAINERS = [BannerStateContainer];

const getForgePageBannerPresetHeight = (): number => {
	try {
		const config = JSON.parse(
			StatsigConfigurations.getString('confluence_forge_banner_preset_height', ''),
		) as { presetHeight?: unknown };

		return typeof config.presetHeight === 'number' ? config.presetHeight : 0;
	} catch {
		return 0;
	}
};

export type MainLayoutComponentProps = {
	/** Keep the content within the page layout so it can own scrolling. */
	fillContainer?: boolean;
	children: React.ReactElement;
	enableNavigation?: boolean;
	disableMinWidth?: boolean;
	screen?: MainLayoutScreenType;
	navView?: string;
	editorPreloader?: () => Promise<() => EditorPreloaderType>;
	isViewPage?: boolean;
	isEdit?: boolean;
};

type MainLayoutInnerContentProps = MainLayoutComponentProps & {
	isLivePage: boolean;
	ommitComponentsNotNeededForPdfExport: boolean;
	renderServerPlaceholder: boolean;
};

const MainLayoutInnerContent: FC<MainLayoutInnerContentProps> = ({
	fillContainer,
	enableNavigation = true,
	disableMinWidth,
	screen,
	isViewPage = false,
	isEdit,
	children,
	isLivePage,
	ommitComponentsNotNeededForPdfExport,
	renderServerPlaceholder,
}) => {
	const {
		shouldShowATLGeneralWebPanel,
		isATLGeneralWebPanelFixed,
		fixedATLGeneralWebPanelRef,
		shouldHidePanels,
	} = useATLGeneralWebPanelObserver(isViewPage, isEdit);

	const { isForgePageBannerFixed, fixedForgePageBannerRef, shouldHideForgePageBanner } =
		useForgePageBannerObserver(isViewPage, true, isEdit);

	const rightSidebarContext = useRightSidebarContext();
	const [enableRightSidebarCSS, setEnableRightSidebarCSS] = useState(false);
	const shouldRenderATLGeneralWebPanel =
		shouldShowATLGeneralWebPanel && !ommitComponentsNotNeededForPdfExport;
	const shouldOmitATLFooterWebPanel =
		isEdit || isLivePage || ommitComponentsNotNeededForPdfExport || renderServerPlaceholder;

	const topElementStyle = {};
	if (screen) {
		for (const key in screen) {
			if (screen.hasOwnProperty(key)) {
				if (key.startsWith('background')) {
					topElementStyle[key] = screen[key];
				}
			}
		}
	}

	const forgePageBannerInitialHeight = getForgePageBannerPresetHeight();
	const shouldUseForgePageBannerInitialHeight = forgePageBannerInitialHeight > 5;

	useEffect(() => {
		if (rightSidebarContext.getCurrent()) {
			setEnableRightSidebarCSS(true);
		}
	}, [rightSidebarContext]);

	const AtlGeneralWebPanel = isATLGeneralWebPanelFixed ? (
		<Fragment>
			<div
				css={[
					fixedWebPanelWrapperStyles['default'],
					!shouldHidePanels && fixedWebPanelWrapperStyles['showPanel'],
				]}
				ref={fixedATLGeneralWebPanelRef}
				data-test-id="fixed-atl-general-wrapper"
				data-connect-container="fixed-atl-general-wrapper"
			>
				<WebPanelLocation location={ATL_GENERAL} fetchPolicy="cache-first" />
			</div>
			<div css={fixedWebPanelSpacerStyles} />
			{renderServerPlaceholder && (
				<BreakoutSSRInlineScript noOpSSRInlineScript={renderServerPlaceholder} />
			)}
		</Fragment>
	) : (
		<div
			css={[
				webPanelWrapperStyles['showPanel'],
				shouldHidePanels && webPanelWrapperStyles['hidePanel'],
			]}
			data-test-id="atl-general-wrapper"
		>
			<WebPanelLocation location={ATL_GENERAL} fetchPolicy="cache-first" />
		</div>
	);

	const ForgePageBannerExtensionPoint = (
		<>
			<div
				data-test-id={
					isForgePageBannerFixed
						? 'fixed-forge-page-banner-wrapper-compiled'
						: 'forge-page-banner-wrapper-compiled'
				}
				css={[
					isForgePageBannerFixed && fixedWebPanelWrapperStyles['default'],
					isForgePageBannerFixed &&
						!shouldHideForgePageBanner &&
						fixedWebPanelWrapperStyles['showPanel'],
					!isForgePageBannerFixed && webPanelWrapperStyles['showPanel'],
					!isForgePageBannerFixed &&
						shouldHideForgePageBanner &&
						webPanelWrapperStyles['hidePanel'],
				]}
				ref={fixedForgePageBannerRef}
				{...(shouldUseForgePageBannerInitialHeight
					? { style: { minHeight: `${forgePageBannerInitialHeight}px` } }
					: {})}
			>
				<RenderPageBannerForgeApps />
			</div>
			{isForgePageBannerFixed && (
				<div
					css={fixedForgeBannerSpacerStyles}
					{...(shouldUseForgePageBannerInitialHeight
						? { style: { minHeight: `${forgePageBannerInitialHeight}px` } }
						: {})}
				/>
			)}
		</>
	);

	const mainLayoutContent = (
		<div
			css={[
				mainContentContainerStyles['default'],
				fillContainer && fg('rovo_fps_fix_duplicate_scrollbars') && fillContainerStyles.container,
				!enableRightSidebarCSS &&
					!(disableMinWidth || !enableNavigation) &&
					mainContentContainerStyles['addMinWidth'],
			]}
			// eslint-disable-next-line @atlaskit/ui-styling-standard/no-classname-prop
			className="confluence-main-content"
			data-test-id="confluence-main-content"
			data-vc="confluence-main-content-compiled"
		>
			{/* Extension point for rendering Connect apps at the top of the main layout */}
			{shouldRenderATLGeneralWebPanel && AtlGeneralWebPanel}
			{/* Extension point for rendering Forge apps at the top of the main layout. */}
			{ForgePageBannerExtensionPoint}
			{/*Banner to allow user to switch from desktop to mobile version*/}
			<ReturnToMobile />

			<>
				<Subscribe to={BANNER_STATE_CONTAINERS}>
					{() => (
						<div
							css={[
								contentBodyWrapperStyles,
								fillContainer &&
									fg('rovo_fps_fix_duplicate_scrollbars') &&
									fillContainerStyles.body,
							]}
						>
							<ContentBody
								css={
									fillContainer &&
									fg('rovo_fps_fix_duplicate_scrollbars') &&
									fillContainerStyles.body
								}
								data-test-id="content-body"
								data-vc="content-body-compiled"
								id="content-body"
								style={topElementStyle}
							>
								{children}
							</ContentBody>
						</div>
					)}
				</Subscribe>
				{/* Hides the panel's UI in non-page content types but keeps the content (vendors often use background scripts for this footer extension point) */}
				{!shouldOmitATLFooterWebPanel && (
					<div
						css={[
							webPanelWrapperStyles['showPanel'],
							shouldHidePanels && webPanelWrapperStyles['hidePanel'],
						]}
						data-test-id="atl-footer-wrapper"
					>
						<WebPanelLocation
							location={ATL_FOOTER}
							// Refetching after hydration returns Connect markup that differs from what the
							// server rendered, invalidating the hydrated DOM. ATL_GENERAL passes `cache-first`
							// for the same reason.
							fetchPolicy="cache-first"
						/>
					</div>
				)}
				{/* Many Connect apps use atl.general and atl.footer Web Panels to run scripts across all Confluence pages.
				Forge background scripts provide the same functionality for apps on the Forge platform */}
				<RenderBackgroundScriptApps />
			</>
		</div>
	);

	return children ? (
		<ErrorBoundary attribution={Attribution.BACKBONE}>
			{/* TODO: remove this when confluence-previews no longer used */}
			{/* eslint-disable-next-line @atlaskit/ui-styling-standard/no-global-styles */}
			<style
				// eslint-disable-next-line react/no-danger
				dangerouslySetInnerHTML={{
					__html: `.confluence-main-content .cp-container [role='dialog'] { z-index: 350; }`,
				}}
			/>
			{mainLayoutContent}
		</ErrorBoundary>
	) : null;
};

export const MainLayoutInnerComponent: FC<MainLayoutComponentProps> = (props) => {
	const { isViewPage = false, isEdit } = props;
	const isLivePage = useIsCurrentPageLive();
	const browserLocation =
		typeof globalThis.window?.location !== 'undefined' ? globalThis.window.location : undefined;
	const isPdfExport =
		browserLocation !== undefined && Boolean(PDF_EXPORT_PAGE.match(browserLocation.pathname));
	const ommitComponentsNotNeededForPdfExport = isPdfExport;
	const renderServerPlaceholder = useIsSSROrHydrating();
	let webPanelBatchLocations: string[] = [];
	if (!ommitComponentsNotNeededForPdfExport) {
		if (isViewPage || isEdit) {
			webPanelBatchLocations = [ATL_GENERAL, ATL_FOOTER];
		}
	}
	const shouldRenderWebPanelBatchProvider =
		webPanelBatchLocations.length > 0 &&
		expValEquals('web_panel_query_batch_call', 'isEnabled', true);
	const content = (
		<MainLayoutInnerContent
			{...props}
			isLivePage={isLivePage}
			ommitComponentsNotNeededForPdfExport={ommitComponentsNotNeededForPdfExport}
			renderServerPlaceholder={renderServerPlaceholder}
		/>
	);

	return shouldRenderWebPanelBatchProvider ? (
		<WebPanelLocationBatchProvider locations={webPanelBatchLocations}>
			{content}
		</WebPanelLocationBatchProvider>
	) : (
		content
	);
};

export const MainLayoutComponent: FC<MainLayoutComponentProps> = (props) => {
	const { editorPreloader, navView, enableNavigation = true } = props;

	const [preloader, setPreloader] = useState<EditorPreloaderType>();

	const setEditorPreloader = async () => {
		if (editorPreloader) {
			const newPreloader = await editorPreloader();
			setPreloader(newPreloader);
		}
	};

	useEffect(() => {
		void setEditorPreloader();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	let content = (
		<SuspenseWithDataFetcher dataFetcherName="default">
			<MainLayoutInnerComponent {...props} />
		</SuspenseWithDataFetcher>
	);

	content = (
		<>
			{enableNavigation ? (
				<PageLayout
					editorPreloader={preloader}
					view={navView as SideNavView}
					fillContainer={props.fillContainer}
				>
					{content}
				</PageLayout>
			) : (
				content
			)}
			<PerformanceEnd subject={PERFORMANCE_SUBJECT_mainLayout} />
		</>
	);

	return <ConfluencePreviewPanelsProvider>{content}</ConfluencePreviewPanelsProvider>;
};
